import { useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';

import { Environment } from '@playq/octopus-common';
import { AppLadderId, LadderDimensionsResponse, LadderTemplate, TeamBucket } from '@playq/services-clerk';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { IUseQueryOptionsExtended, useEitherQuery } from '/api/service-hooks';
import { services2 } from '/api/services2';

import { laddersQueryTags } from './useQueryLadders';

const emptyArray: [] = [];

export const queryTeamsLadderInstanceDimensionsKeys = laddersQueryTags.concat('queryTeamLadderInstancesDimensions');

export const useQueryTeamLadderInstancesDimensions = (
  env: Environment,
  ladder: LadderTemplate | undefined,
  iterator: OffsetLimit = new OffsetLimit({ offset: 0, limit: 10 }),
  options?: IUseQueryOptionsExtended<LadderDimensionsResponse | undefined, GenericFailure | Error>
) => {
  const id = ladder?.id;

  const keys = useMemo(() => queryTeamsLadderInstanceDimensionsKeys.concat(env, id, iterator), [env, id, iterator]);

  const isValid = useMemo(
    () => id?.appId !== undefined && id?.ladderId !== undefined && ladder?.ladderType instanceof TeamBucket,
    [id, ladder?.ladderType]
  );

  const enabled = useMemo(() => (options?.enabled ?? true) && isValid, [options?.enabled, isValid]);

  const res = useEitherQuery(
    keys,
    () => services2.teamScoreSupportService.queryTeamLadderInstanceDimensions(env, id as AppLadderId, iterator),
    {
      placeholderData: keepPreviousData,
      ...options,
      enabled,
    }
  );

  const dimensions = useMemo(() => res.data?.dimensions, [res.data?.dimensions]);

  return { ...res, tags: keys, dimensions: dimensions || emptyArray };
};
